import { isAndroidApp, isWindowsApp } from "../platform";

export const SYSTEM_NOTIFICATION_STATE_KEY = "ql_system_notification_state_v1";
const MAX_REMEMBERED_IDS = 200;
const ANDROID_CHANNEL_ID = "quick-learning-updates";

const notificationId = value => {
  const parsed = Number(value);
  if (Number.isSafeInteger(parsed) && parsed > 0) return (parsed % 2000000000) + 1000;
  return Math.abs(String(value).split("").reduce((hash, char) => ((hash * 31) + char.charCodeAt(0)) | 0, 0)) + 1000;
};

export function selectNewSystemNotifications(notifications, rawState, now = Date.now()) {
  const list = Array.isArray(notifications) ? notifications : [];
  const currentIds = list.map(item => String(item.id));
  let state;
  try {
    state = rawState ? JSON.parse(rawState) : null;
  } catch {
    state = null;
  }

  if (!state?.initialized) {
    return {
      notifications: [],
      state: JSON.stringify({ initialized: true, ids: currentIds.slice(0, MAX_REMEMBERED_IDS), checkedAt: now }),
    };
  }

  const remembered = new Set(Array.isArray(state.ids) ? state.ids.map(String) : []);
  const fresh = list.filter(item => !item.is_read && !remembered.has(String(item.id)));
  const ids = [...new Set([...currentIds, ...remembered])].slice(0, MAX_REMEMBERED_IDS);
  return {
    notifications: fresh,
    state: JSON.stringify({ initialized: true, ids, checkedAt: now }),
  };
}

async function androidPlugin() {
  return window.Capacitor?.Plugins?.LocalNotifications || null;
}

export async function prepareSystemNotifications() {
  try {
    if (isAndroidApp) {
      const plugin = await androidPlugin();
      if (!plugin) return false;
      const permission = await plugin.checkPermissions();
      const result = permission.display === "granted" ? permission : await plugin.requestPermissions();
      if (result.display !== "granted") return false;
      await plugin.createChannel?.({
        id: ANDROID_CHANNEL_ID,
        name: "Informations Quick Learning",
        description: "Lives, nouveaux contenus et informations liées à vos manuels",
        importance: 5,
        visibility: 1,
      });
      return true;
    }
    if (isWindowsApp) {
      const { isPermissionGranted, requestPermission } = await import("@tauri-apps/plugin-notification");
      return await isPermissionGranted() || await requestPermission() === "granted";
    }
  } catch {
    return false;
  }
  return false;
}

export async function sendSystemNotification(notification) {
  if (!notification?.title) return false;
  try {
    if (!(await prepareSystemNotifications())) return false;
    if (isAndroidApp) {
      const plugin = await androidPlugin();
      await plugin.schedule({
        notifications: [{
          id: notificationId(notification.id),
          title: notification.title,
          body: notification.content || "Une nouvelle information est disponible.",
          channelId: ANDROID_CHANNEL_ID,
          schedule: { at: new Date(Date.now() + 500) },
          extra: { source: "quick-learning", notificationId: notification.id },
        }],
      });
      return true;
    }
    if (isWindowsApp) {
      const { sendNotification } = await import("@tauri-apps/plugin-notification");
      sendNotification({
        title: notification.title,
        body: notification.content || "Une nouvelle information est disponible.",
      });
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

export async function forwardNewNotificationsToSystem(notifications, storage = window.localStorage) {
  if (!isAndroidApp && !isWindowsApp) return [];
  const selection = selectNewSystemNotifications(
    notifications,
    storage.getItem(SYSTEM_NOTIFICATION_STATE_KEY),
  );
  storage.setItem(SYSTEM_NOTIFICATION_STATE_KEY, selection.state);
  await Promise.all(selection.notifications.map(sendSystemNotification));
  return selection.notifications;
}
